import React from 'react';
import { useStringsContext } from '../../../contexts/strings';
import { useBreadcrumbs } from '../../../hooks/useBreadcrumbs';
import SceneHeader from '../../common/scene/scene-header';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Button, IconButton, Paper, Stack, Typography } from '@mui/material';
import { useParams } from 'react-router';
import { SxStyle } from '../../../types';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { useSegments } from '../../../hooks/useSegments';
import SegmentForm from './components/segment-form';
import ScenePage from '../../common/scene/scene-page';
import moment from 'moment';
import { useSnackbar } from 'notistack';
import { Segment, Source } from '../../../types/segment';
import { StringType } from '../../../sv-strings';

const styles: SxStyle = {
  container: (theme) => ({
    width: '100%',
    marginTop: theme.spacing(4),
  }),
  dataGrid: (theme) => ({
    '& .MuiDataGrid-root .MuiDataGrid-cell:focus, .MuiDataGrid-cell:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeader:focus, .MuiDataGrid-columnHeader:focus-within': {
      outline: 'none',
    },
    '& .MuiDataGrid-columnHeaderTitleContainer, .MuiDataGrid-columnHeaderTitleContainerContent': {
      width: '100%',
    },
    '& .MuiDataGrid-row:nth-of-type(odd)': {
      backgroundColor: theme.palette.shades.p10,
    },
  }),
};

/** Describes how a poll segment was picked, e.g. "Vinnare – 3 av 412 (slumpat)". */
function getPollSummary(segment: Segment, strings: StringType): string {
  const { poll } = segment;
  if (!poll) return '';

  const summary = strings.pushNotifications.segmentPollSummary
    .replace(
      '$audience',
      poll.audience === 'WINNERS'
        ? strings.pushNotifications.segmentPollWinners
        : strings.pushNotifications.segmentPollParticipants
    )
    .replace('$count', String(segment.idsCount ?? 0))
    .replace('$total', String(poll.qualifiedCount));

  return poll.pick && poll.limit
    ? `${summary} (${strings.pushNotifications[`segmentPollPick${poll.pick}`]})`
    : summary;
}

export default function PushNotificationSegments() {
  const { strings } = useStringsContext();

  const [selectedSegmentId, setSelectedSegmentId] = React.useState<string | null>(null);
  const [, page] = location.pathname.split('/');
  const params = useParams<{ abbrv: string }>();
  const selectedAbbrv = params.abbrv || null;
  const { query, mutation } = useSegments(selectedAbbrv || '');
  const { enqueueSnackbar } = useSnackbar();

  const onDeleteSegment = React.useCallback(
    async (id: string) => {
      try {
        await mutation.mutateAsync({ id, action: 'delete' });
        enqueueSnackbar(strings.messages.deleteSuccess, { variant: 'success' });
        return true;
      } catch (error) {
        console.error(error);
        enqueueSnackbar(strings.messages.deleteError, { variant: 'error' });
        return false;
      }
    },
    [mutation, enqueueSnackbar, strings]
  );

  const breadCrumps = useBreadcrumbs({
    beforeLinks: [],
    afterLinks: [
      {
        to: `/${page}/${selectedAbbrv}`,
        text: strings.pushNotifications.title,
      },
    ],
  });

  const segments = query?.data || [];
  const selectedSegment = segments.find((segment: any) =>
    [segment.id, segment.segmentId].includes(selectedSegmentId)
  );

  const columns: GridColDef[] = React.useMemo(
    () => [
      {
        field: 'name',
        headerName: strings.pushNotifications.segmentsName,
        width: 160,
      },
      {
        field: 'description',
        headerName: strings.pushNotifications.segmentsDescription,
        width: 400,
      },
      {
        field: 'source',
        headerName: strings.pushNotifications.segmentsSource,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams) =>
          params.row.source === Source.POLL ? (
            <Stack justifyContent="center" sx={{ height: '100%', lineHeight: 1.4 }}>
              <Typography variant="body2">{strings.pushNotifications.optionSegmentPOLL}</Typography>
              <Typography variant="caption" color="text.secondary">
                {getPollSummary(params.row, strings)}
              </Typography>
            </Stack>
          ) : (
            params.row.source
          ),
      },
      {
        field: 'createdAt',
        headerName: strings.pushNotifications.segmentsCreatedAt,
        minWidth: 100,
        flex: 1,
        renderCell: (params: GridRenderCellParams) => (
          <>{params.row.createdAt ? moment(params.row.createdAt).format('YYYY-MM-DD HH:MM') : ''}</>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        renderCell: (params: GridRenderCellParams) => (
          <>
            {params.row.type === 'SEGMENT' ? (
              <>
                <IconButton
                  color="primary"
                  onClick={() => {
                    setSelectedSegmentId(params.row.id);
                  }}
                >
                  <EditIcon />
                </IconButton>
                <IconButton color="error" onClick={() => onDeleteSegment(params.row.id)}>
                  <DeleteIcon />
                </IconButton>
              </>
            ) : null}
          </>
        ),
      },
    ],
    [onDeleteSegment, strings]
  );
  return (
    <ScenePage title={strings.pushNotifications.pushNotificationSegments} breadCrumps={breadCrumps}>
      {selectedSegmentId !== null ? (
        <SegmentForm
          segment={
            selectedSegment || {
              id: '',
              name: '',
              description: '',
              type: 'SEGMENT',
            }
          }
          onSave={async (data, isFormData) => {
            const res = await mutation.mutateAsync({
              body: data,
              id: selectedSegment?.id,
              isFormData,
              action: selectedSegmentId ? 'edit' : 'create',
            });
            if (res?.id) {
              setSelectedSegmentId(res.id);
            }
            return;
          }}
          onDelete={async () => {
            if (selectedSegment?.id && (await onDeleteSegment(selectedSegment.id))) {
              setSelectedSegmentId(null);
            }
          }}
          onClose={() => {
            setSelectedSegmentId(null);
          }}
          isPending={mutation.isPending}
        />
      ) : null}

      <SceneHeader
        title={strings.pushNotifications.pushNotificationSegments}
        backButton={`/pushdashboard/${selectedAbbrv}`}
      >
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => {
            setSelectedSegmentId('');
          }}
        >
          {strings.pushNotifications.addSegmentButton}
        </Button>
      </SceneHeader>

      <Paper elevation={2} sx={styles.container}>
        <DataGrid
          sx={styles.dataGrid}
          rows={segments}
          columns={columns}
          autoPageSize={false}
          disableRowSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableColumnResize
          disableVirtualization
          hideFooter
          loading={query.isLoading || mutation.isPending}
          initialState={{
            sorting: {
              sortModel: [],
            },
          }}
        />
      </Paper>
    </ScenePage>
  );
}
